import { useRoute, useRouter, type RouteLocationNormalizedLoaded, type Router } from 'vue-router'
import type { ProductLiteCategorized } from '@lib/models/product'
import { logInfo, logError, scopedLogger } from '@lib/utils/logUtils'
import { useAppStore } from '@stores/app.store'
import { aopService } from '@lib/services/backend/aop/aop.service'
import { BackendCustomException } from '@lib/models/internal/errors'
import { LoginMethod } from '@lib/models/gtm'
import { useErrorApi } from './useErrorApi'
import { useGtm } from './useGtm'

const log = scopedLogger('@composables/useAopCoupon.ts')

export type CouponErrorType = 'aopCouponAlreadyUsed' | 'aopCouponError'

export type ProcessCouponResult =
  | { status: 'ok' }
  | { status: 'error'; type: CouponErrorType }
  // 401 is handled globally (session dialog), the caller must not show a coupon error
  | { status: 'unauthorized' }

// no products is a coupon error; a single product goes straight to its detail, several to the list
const handleCouponProducts = async ({
  coupon,
  products,
  router,
  route,
  store,
  trackGetOnline
}: {
  coupon: string
  products: ProductLiteCategorized[]
  router: Router
  route: RouteLocationNormalizedLoaded
  store: ReturnType<typeof useAppStore>
  trackGetOnline: (coupon: string) => void
}): Promise<ProcessCouponResult> => {
  if (!products.length) {
    logInfo(log, 'Coupon has no products')
    return { status: 'error', type: 'aopCouponError' }
  }

  trackGetOnline(coupon)
  if (products.length === 1) {
    const { idCategory, identifier } = products[0]
    logInfo(log, 'Navigating to product detail with aopCoupon = ' + coupon)
    await router.push({
      name: 'product',
      params: { categoryId: idCategory, productId: identifier },
      query: { aopCoupon: coupon }
    })
    return { status: 'ok' }
  }

  store.setAopCouponProducts(products)
  logInfo(log, 'Navigating to aop-coupon-products with aopCoupon = ' + coupon)
  await router.push({
    name: 'aop-coupon-products',
    query: { aopCoupon: coupon, fromPath: route.path }
  })
  return { status: 'ok' }
}

export const useAopCoupon = () => {
  const store = useAppStore()
  const router = useRouter()
  const route = useRoute()
  const { trackLogin, trackGetOnline } = useGtm()
  const { logErrorApi } = useErrorApi()

  const toErrorResult = (error: unknown): ProcessCouponResult => {
    const { errorKey } = logErrorApi('processCoupon', error, log)
    if (errorKey === 'unauthorized') return { status: 'unauthorized' }
    return {
      status: 'error',
      type: errorKey === BackendCustomException[610] ? 'aopCouponAlreadyUsed' : 'aopCouponError'
    }
  }

  /**
   * Validates a coupon. When the coupon carries an mcId (identified coupon) and
   * nobody is logged, logs that user in best effort, in parallel with the coupon
   * validation: a failed login never blocks nor fails the coupon.
   */
  const processCoupon = async ({
    coupon,
    mcId,
    sessionId
  }: {
    coupon: string
    mcId?: string
    sessionId: string
  }): Promise<ProcessCouponResult> => {
    logInfo(log, 'Starting coupon process')

    const shouldLogin = !!mcId && !store.user
    if (shouldLogin) logInfo(log, 'Starting to identificate with mcId = ' + mcId)

    // both requests settle before navigating, so the user is already set on the next page
    const [loginSettled, couponSettled] = await Promise.allSettled([
      shouldLogin ? store.logInUserAction({ mcId }) : Promise.resolve(undefined),
      aopService.aopCheckCoupon(coupon, sessionId)
    ])

    if (shouldLogin) {
      if (loginSettled.status === 'fulfilled' && loginSettled.value) trackLogin(LoginMethod.COUPON)
      else logError(log, 'User could not be logged from coupon, continuing without identification')
    }

    if (couponSettled.status === 'rejected') return toErrorResult(couponSettled.reason)

    try {
      const { products } = couponSettled.value
      return await handleCouponProducts({ coupon, products, router, route, store, trackGetOnline })
    } catch (error) {
      // e.g. the navigation fails: still a coupon error, never an unhandled rejection
      return toErrorResult(error)
    }
  }

  return { processCoupon }
}
